import _ from "lodash";
import L from "leaflet";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Marker } from "react-leaflet";
import { LeafletMap } from "./LeafletMap";
import { isPointWithinUSBounds } from "../utils/functions";
import "./StreamTableMap.scss";

const greenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const blueIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface IStreamTableMap {
  headerRenderer(listPins: number, visiblePins: number): void;
  cellRenderer(selectedPin: any, data: any): void;
  locationAttribute: string;
  pinsQuery: any;
  totalHits: number;
  tableRenderer(data: { tableProps: any; containerProps: any; columns: Array<any> }): any;
  onMapMove(visiblePins: Array<any>): void;
  rows?: any;
}

export const StreamTableMap = ({
  cellRenderer,
  headerRenderer,
  tableRenderer,
  pinsQuery,
  locationAttribute,
  totalHits,
  onMapMove,
  rows,
}: IStreamTableMap) => {
  const [selectedPin, setSelectedPin] = useState<string>();
  const [map, setMap] = useState<any>();
  const [bounds, setBounds] = useState<L.LatLngBounds | undefined>();

  const onMove = useCallback(() => {
    const bounds: L.LatLngBounds = map.getBounds();
    const visiblePins: Array<any> = pins
      .filter((item: any) => {
        const point = _.get(item, "_source.locationPoint");
        return !!point && bounds.contains(L.latLng(point.lat, point.lon));
      })
      .map((item: any) => item._id);
    onMapMove(visiblePins);
  }, [map, onMapMove]);

  const totalPins = _.get(pinsQuery, "data.hits.total.value", 0);
  const columns: any = useMemo(
    () => [
      {
        id: "pins",
        Header: () => headerRenderer(totalHits, totalPins),
        Cell: (data: any) => cellRenderer(selectedPin, data),
        style: {
          padding: 5,
        },
        flexGrow: 1,
      },
    ],
    [totalPins, totalHits, selectedPin],
  );
  const pins = useMemo(() => {
    return _.flatten(_.get(pinsQuery, "data.hits.hits", [])).filter((item: any) => !!_.get(item, locationAttribute));
  }, [pinsQuery.data]);

  const pinMarkers = useMemo(() => {
    return pins.map((item: any) => {
      //Take only the ID before the underscore so it matches the table cell ID
      const id = item._id.split("_")[0];
      const point = _.get(item, locationAttribute);
      const isSelected = selectedPin?.toString() === id.toString();
      const icon = isSelected ? greenIcon : blueIcon;
      return (
        <Marker
          key={item._id}
          position={[point.lat, point.lon]}
          icon={icon}
          ref={(e) => {
            if ((e as any)?.on) {
              (e as any).on("click", () => {
                map?.setView([point.lat, point.lon], 17);
                setSelectedPin(id);
              });
            }
          }}
        />
      );
    });
  }, [map, pins, selectedPin, setSelectedPin]);

  const containerProps = {
    style: {
      display: "block",
      height: "100%",
    },
  };

  const tableProps = {
    noRowsRenderer: () => <div style={{ padding: "16px", display: "block", textAlign: "center" }}>No results</div>,
    headerHeight: 30,
    rowHeight: 90,
    width: 300,
    onRowClick: (data: any) => {
      const id = data.rowData.original.caseId;
      setSelectedPin(id.toString());
    },
    rowClassName: ({ index }: { index: number }) => {
      if (selectedPin === undefined) return "";
      const rowIsSelected = selectedPin == rows[index]?.caseId;
      return rowIsSelected ? "code-crm-map-selected-cell" : "code-crm-map-unselected-cell";
    },
  };

  // Set map boundary
  useEffect(() => {
    const filteredPins = pins.filter((item: any) => !!_.get(item, locationAttribute));
    if (!_.isEmpty(filteredPins)) {
      const bounds = L.latLngBounds(
        pins
          .filter((item: any) => {
            const point = _.get(item, locationAttribute);
            return !!point && isPointWithinUSBounds(point.lat, point.lon);
          })
          .map((item: any) => {
            const point = _.get(item, locationAttribute);
            return L.latLng(point.lat, point.lon);
          }),
      );
      setBounds(bounds);
    }
  }, [pins]);

  // Register map listeners
  useEffect(() => {
    map?.on("moveend", onMove);
    return () => {
      map?.off("moveend", onMove);
    };
  }, [map, onMove]);

  return (
    <>
      <div
        style={{
          borderLeft: "1px solid gray",
          position: "absolute",
          display: "block",
          right: 0,
          top: 0,
          width: "350px",
          height: "100%",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        {tableRenderer({ tableProps, containerProps, columns })}
      </div>
      {bounds && (
        <LeafletMap
          bounds={bounds}
          zoom={14}
          style={{ height: "100%", width: "calc(100% - 350px)" }}
          whenCreated={setMap}
        >
          {pinMarkers}
        </LeafletMap>
      )}
    </>
  );
};
